'use client';

import clsx from 'clsx';
import { useEffect } from 'react';

import { Button } from '@/components/button/button';
import Modal from '@/components/modal/modal';
import { Heading } from '@/components/typography/heading';
import { useExampleQuery } from '@/hooks/queries/useEvaluations';
import { isStructuredMessage, Result } from '@/types/evals';

import styles from './modalExampleShow.module.scss';

type Props = {
  exampleId: string;
  sourceId: string;
  sourceVersion: number | 'latest';
  metricResults: { [metricKey: string]: Result };
  metricKeys: string[];
  metricProviderNames: Record<string, string>;
  onCloseAction: () => void;
};

export default function ModalExampleShow( { exampleId, sourceId, sourceVersion, metricResults, metricKeys, metricProviderNames, onCloseAction }: Props ) {
  const { data: sExample, isLoading: sIsLoading, isError } = useExampleQuery( sourceId, sourceVersion, exampleId );

  useEffect( () => {
    const onKeyDown = ( e: KeyboardEvent ) => {
      if( e.key === 'Escape' ) {
        onCloseAction();
      }
    };
    document.addEventListener( 'keydown', onKeyDown );
    return () => document.removeEventListener( 'keydown', onKeyDown );
  }, [ onCloseAction ] );

  return (
    <Modal
      resizable
      modalType="example-details"
      initialSize="large"
      minSize={ { width: 600, height: 400 } }
    >
      <div className={ styles.content } data-testid="example-details-modal">
        <Heading as="h2" size="md">Example: { exampleId }</Heading>
        <div className={ clsx( 'layout__col', styles.doms ) } data-testid="example-details-content">
          { sIsLoading && <div className={ styles.loading }>Loading...</div> }
          { isError && <div className={ styles.error }>Unable to load example data</div> }

          { sExample && (
            <>
              <div className={ styles.section }>
                <Heading as="h3" size="sm">Conversation</Heading>
                <div className={ styles.messages }>
                  { sExample.data.map( ( msg, i ) => (
                    <div key={ i } className={ styles.message }>
                      <span className={ styles.message_role }>{ msg.user }</span>
                      { isStructuredMessage( msg )
                        ? <pre className={ styles.message_data }>{ JSON.stringify( msg.data, null, 2 ) }</pre>
                        : <p className={ styles.message_text }>{ msg.message }</p> }
                    </div>
                  ) ) }
                </div>
              </div>

              <div className={ clsx( styles.section, styles.section_divider ) }>
                <Heading as="h3" size="sm">Metric Results</Heading>
                <div className={ styles.metric_results }>
                  { metricKeys.map( metricKey => {
                    const [ metricProviderId, metricName ] = metricKey.split( '|' );
                    const result = metricResults[ metricKey ];
                    const providerName = metricProviderNames[ metricProviderId ] || metricProviderId;
                    const score = result?.score ? parseFloat( result.score ) : null;
                    const displayScore = score !== null && !isNaN( score ) ? score.toFixed( 3 ) : ( result?.score || '-' );
                    const reason = result?.resultStatusReason || result?.scoreMetaData?.reason;

                    return (
                      <div key={ metricKey } className={ styles.metric_result_item }>
                        <div className={ styles.metric_result_header }>
                          <span className={ styles.metric_result_name }>{ providerName } / { metricName }</span>
                          <span className={ styles.metric_result_score }>{ displayScore }</span>
                          { result?.resultStatus && (
                            <span className={ styles.metric_result_status }>{ result.resultStatus }</span>
                          ) }
                        </div>
                        { reason && (
                          <p className={ styles.metric_result_reason }>{ reason }</p>
                        ) }
                      </div>
                    );
                  } ) }
                </div>
              </div>
            </>
          ) }
        </div>
        <div className={ clsx( 'layout__row', 'pad', 'flex_right' ) }>
          <Button variant='primary' onClick={ onCloseAction } data-testid="example-details-close-button">Close</Button>
        </div>
      </div>
    </Modal>
  );
}
